import { isNullOrUndefined } from 'rewire-common';
import { Form, IFormContext, arraySearch, validator, field } from 'rewire-ui';
import { capitalCase } from 'change-case';
import {
  batchStatusSearcher,
  timecardTestStatusSearcher,
  scheduleFilterInputIdFromKey,
  MessageLevels,
  AvailableBalances,
  FieldFilter,
  INameLike,
} from '../DataTypes';
import DataQueries from '../graphql/Queries';
import UdfsFilterDialogModel from './UdfsFilterDialogModel';
import { RouteManagerBase } from '../main-navigation/RouteManager';
import { getNameSearcher } from '../utility/Searchers';
import scheduleControlsModel, { IScheduleFilters } from './ScheduleControlsModel';
import { DateRange } from 'rewire-common';

export class ScheduleFilterDialogModel extends UdfsFilterDialogModel {
  constructor() {
    super('Filter By');
  }

  protected _createForm(): Form {
    const timecardStatusSearcher = {
      async search(_search?: string): Promise<string[]> {
        return await DataQueries.getPossibleTimecardStatuses();
      },
      map(item: any) {
        return (item && !Array.isArray(item) && capitalCase(item)) || '';
      },
    };

    const timeOffStatusSearcher = {
      async search(_search?: string): Promise<string[]> {
        return await DataQueries.getPossibleTimeOffRequestStatuses();
      },
      map(item: any) {
        return item === undefined ? '' : capitalCase(item);
      },
    };

    const groupFilter = () =>
      this.form.field.areas.value?.length
        ? ({ field: 'areaId', in: this.form.field.areas.value.map((area: INameLike) => area.id) } as FieldFilter)
        : undefined;
    const reasonFilter = () => ({ field: 'category', in: scheduleControlsModel.reasonFilterCategories }) as FieldFilter;
    const areaSearcher = getNameSearcher('areas');
    const groupSearcher = getNameSearcher('groups', groupFilter);
    const occupationSearcher = getNameSearcher('occupations');
    const employeeSearcher = getNameSearcher('employees');
    const generatorSearcher = getNameSearcher('generators');
    const templateSearcher = getNameSearcher('templates');
    const reasonSearcher = getNameSearcher('reasons', reasonFilter);
    const batchSearcher = getNameSearcher('batches');
    const timeOffTypeSearcher = getNameSearcher('timeOffTypes');
    const levelSearcher = arraySearch(
      Object.values(MessageLevels),
      (item: any) => (item && !Array.isArray(item) && capitalCase(item)) || '',
    );
    const boolSearcher = arraySearch([true, false], (item: any) => (!isNullOrUndefined(item) ? (item ? 'Yes' : 'No') : ''));
    const availableBalanceSearcher = arraySearch(
      Object.values(AvailableBalances),
      (item: any) => (item && !Array.isArray(item) && capitalCase(item)) || '',
    );
    const tagSearcher = {
      async search(search?: string) {
        return await DataQueries.getTags(RouteManagerBase.current.documentType || 'documents', search);
      },
      map(item?: any) {
        return (item && !Array.isArray(item) && item) || '';
      },
    };

    return Form.create(
      (form: IFormContext) => ({
        areas: form
          .multiselectautocomplete(areaSearcher, { chipLimit: 3 })
          .label('Area')
          .placeholder('select Area(s)')
          .disabled(() => scheduleControlsModel.mode === 'group')
          .autoFocus(),
        groups: form
          .multiselectautocomplete(groupSearcher, { chipLimit: 3 })
          .label('Group')
          .placeholder('select Group(s)')
          .disabled(() => scheduleControlsModel.mode === 'group'),
        occupations: form
          .multiselectautocomplete(occupationSearcher, { chipLimit: 3 })
          .label('Occupation')
          .placeholder('select Occupation(s)'),
        assignedEmployees: form
          .multiselectautocomplete(employeeSearcher, { chipLimit: 3 })
          .label('Assigned Employee')
          .placeholder('select Assigned Employee(s)'),
        employees: form
          .multiselectautocomplete(employeeSearcher, { chipLimit: 3 })
          .label('Employee')
          .placeholder('select Employee(s)'),
        generators: form
          .multiselectautocomplete(generatorSearcher, { chipLimit: 3 })
          .label('Shift Pattern')
          .placeholder('select Shift Pattern(s)'),
        templates: form
          .multiselectautocomplete(templateSearcher, { chipLimit: 3 })
          .label('Shift Type')
          .placeholder('select Shift Type(s)'),
        reasons: form.multiselectautocomplete(reasonSearcher, { chipLimit: 3 }).label('Reason').placeholder('select Reason(s)'),
        batches: form.multiselectautocomplete(batchSearcher, { chipLimit: 3 }).label('Batch').placeholder('select Batch(es)'),
        levels: form
          .multiselectautocomplete(levelSearcher, { chipLimit: 3 })
          .label('Message Level')
          .placeholder('select Message Level(s)'),
        messageLevels: form
          .multiselectautocomplete(levelSearcher, { chipLimit: 3 })
          .label('Message Level')
          .placeholder('select Message Level(s)'),
        inBatch: form.select(boolSearcher).label('In Batch').placeholder('select In Batch'),
        batchStatus: form.select(batchStatusSearcher).label('Batch Status').placeholder('select Batch Status'),
        timecardStatus: form
          .multiselectautocomplete(timecardStatusSearcher, { chipLimit: 3 })
          .label('Timecard Status')
          .placeholder('select Timecard Status'),
        availableBalance: form.select(availableBalanceSearcher).label('Available Balance').placeholder('select Available Balance'),
        timeOffStatus: form.select(timeOffStatusSearcher).label('Timeoff Status').placeholder('select Timeoff Status'),
        timeOffTypes: form
          .multiselectautocomplete(timeOffTypeSearcher, { chipLimit: 3 })
          .label('Timeoff Types')
          .placeholder('select Timeoff Types'),
        timecardTestStatus: form
          .select(timecardTestStatusSearcher)
          .label('Timecard Test Status')
          .placeholder('select Timecard Test Status'),
        effectiveStart: form
          .date()
          .label('Effective Start')
          .placeholder('select Effective Start')
          .validators(validator('requiredWhenOthersAreNotNull', field('effectiveEnd'))),
        effectiveEnd: form
          .date()
          .label('Effective End')
          .placeholder('select Effective End')
          .validators(validator('requiredWhenOthersAreNotNull', field('effectiveStart')), validator('>=', field('effectiveStart'))),
        lastRanAfter: form.date().label('Last Ran After').placeholder('select Last Ran After'),
        tags: form
          .multiselectautocomplete(tagSearcher, { chipLimit: 3, inputAdd: (v: string) => v })
          .label('Tags')
          .placeholder('enter Tags'),
        search: form.string().label('Search').placeholder('enter Search Text'),
      }),
      {},
      { updateOnChange: true, disableErrors: true },
    );
  }

  protected async _preload(_filters?: IScheduleFilters) {
    // only display those filters applicable to the current Route Manager
    for (const field of this.form.fields) {
      const key = scheduleFilterInputIdFromKey(field.name as any) ?? field.name;
      field.visible = RouteManagerBase.current.permittedFilters.includes(key);
    }
  }

  protected mapToForm(obj: ObjectType): ObjectType {
    const filterFormFieldValues = { ...obj, effectiveStart: obj.effective?.start, effectiveEnd: obj.effective?.end } as ObjectType;
    return super.mapToForm(filterFormFieldValues);
  }

  protected mapToSave(): ObjectType {
    const newFilters = super.mapToSave();
    if (newFilters['effectiveStart'] && newFilters['effectiveEnd']) {
      newFilters['effective'] = new DateRange(newFilters['effectiveStart'], newFilters['effectiveEnd']);
      delete newFilters['effectiveStart'];
      delete newFilters['effectiveEnd'];
    }
    return newFilters;
  }

  protected async _save(filters: ObjectType): Promise<boolean> {
    scheduleControlsModel.scheduleFilters = filters;
    scheduleControlsModel.apply();
    return true;
  }
}

const scheduleFilterDialogModel = new ScheduleFilterDialogModel();
export default scheduleFilterDialogModel;
